/**
 * Calculate unused budget based on budget (given) and planning values
 * @param {Object} row - The accrual data row object
 * @returns {number} The unused budget
 */
export const calculateUnused = (row) => {
  if (!row) return 0;
  const given = Number(row.given) || 0; // Expect number
  const planning = Number(row.planning_gross_cost) || 0; // Expect number
  return given - planning;
};

/**
 * Calculate saving based on planning and actual values
 * @param {Object} row - The accrual data row object
 * @param {number} calculatedActualGrossCost - The pre-calculated actual gross cost for the row
 * @returns {number} The saving amount
 */
export const calculateSaving = (row, calculatedActualGrossCost) => {
  if (!row) return 0;
  if (row.provisional_saving !== undefined && row.provisional_saving !== null) {
    return Number(row.provisional_saving) || 0;
  }
  const planning = Number(row.planning_gross_cost) || 0;
  return planning - calculatedActualGrossCost;
};

/**
 * Format currency values for display
 * @param {number|string} value - The value to format (can be number or string that can be parsed to number)
 * @returns {string} The formatted currency string, or empty string if value is invalid
 */
export const formatCurrency = (value) => {
  // Handle null, undefined, empty string, or whitespace-only strings
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return '';
  }
  
  // Parse the value to ensure it's a valid number
  const number = typeof value === 'number' ? value : parseFloat(value);
  
  // Check if the parsed value is a valid finite number
  if (isNaN(number) || !isFinite(number)) {
    return '';
  }
  
  // Format the number with Vietnamese locale (uses . as thousand separator and , as decimal separator)
  return new Intl.NumberFormat('vi-VN', {
    style: 'decimal',
    minimumFractionDigits: 2, // Always show 2 decimal places
    maximumFractionDigits: 2, // Maximum 2 decimal places
    useGrouping: true        // Use thousand separators
  }).format(number);
};

/**
 * Parse a possibly formatted currency string to a number
 * @param {string|number} value - The value to parse
 * @returns {number} The parsed number
 */
/**
 * Parse a possibly formatted currency string to a number
 * @param {string|number} value - The value to parse (can be string with formatting or number)
 * @returns {number} The parsed number, or 0 if invalid
 */
export const parseCurrency = (value) => {
  // Handle null, undefined, empty string, or whitespace-only strings
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return 0;
  }
  
  // If it's already a number, return it (checking for NaN/Infinity)
  if (typeof value === 'number') {
    return isFinite(value) ? value : 0;
  }
  
  // Convert to string and trim whitespace
  let cleanValue = String(value).trim();
  
  // If the string is empty after trimming, return 0
  if (cleanValue === '') {
    return 0;
  }
  
  // Handle special cases like ' -   '
  if (/^-?\s*$/.test(cleanValue)) {
    return 0;
  }
  
  // Remove currency symbols and any spaces
  cleanValue = cleanValue.replace(/[₫\s]/g, '');
  
  // Handle negative numbers
  const isNegative = cleanValue.startsWith('-');
  cleanValue = cleanValue.replace(/^-/, '');
  
  // Vietnamese number format uses comma as decimal separator and dot as thousand separator
  // Check if the value contains a comma (Vietnamese decimal point)
  const hasCommaDecimal = cleanValue.includes(',');
  
  if (hasCommaDecimal) {
    // Split into integer and decimal parts using comma as separator
    const [integerPart, decimalPart] = cleanValue.split(',');
    
    // Remove all dots (thousand separators) from integer part
    const cleanInteger = integerPart.replace(/\./g, '');
    
    // Replace comma with dot for standard decimal notation
    cleanValue = `${cleanInteger}.${decimalPart}`;
  } else {
    // No comma decimal separator, check if it has dots
    const hasDotSeparator = cleanValue.includes('.');
    
    if (hasDotSeparator) {
      // In Vietnamese format, dots are thousand separators
      // Remove all dots and treat as an integer
      cleanValue = cleanValue.replace(/\./g, '');
    }
  }
  
  // Convert to number
  const number = parseFloat(cleanValue);
  
  // Apply negative sign if needed
  const result = isNegative ? -Math.abs(number) : number;
  
  // Log for debugging
  console.log(`parseCurrency: original=${value}, parsed=${result}`);
  
  // Return the number, or 0 if conversion fails
  return isFinite(result) ? result : 0;
};

/**
 * Calculate actual gross cost from its components
 * @param {Object} row - The accrual data row object
 * @returns {number} The calculated actual gross cost
 */
export const calculateActualGrossCost = (row) => {
  if (!row) return 0;
  
  const netNetCost = Number(row.net_net_cost) || 0; // Expect number
  const whtax = Number(row.whtax) || 0; // Expect number
  const ooscopeFees = Number(row.ooscope_fees) || 0; // Expect number
  const trackingFees = Number(row.tracking_fees) || 0; // Expect number
  const costOnCoupon = Number(row.cost_on_coupon_voucher_etc) || 0; // Expect number
  
  return netNetCost + whtax + ooscopeFees + trackingFees + costOnCoupon;
};

/**
 * Calculate totals for a group of accrual data
 * @param {Array} accrualData - Array of accrual data rows
 * @returns {Object} An object with totals
 */
export const calculateTotals = (accrualData) => {
  if (!accrualData || !accrualData.length) {
    return {
      planning: 0,
      actual: 0,
      unused: 0,
      saving: 0, // Add saving to totals
      given: 0, 
    };
  }
  // The input accrualData should now have currency fields as numbers.
  // console.log('[calculateTotals] Input accrualData (currency fields should be numbers):', JSON.stringify(accrualData.map(r => ({id: r.id, given: r.given, planning_gross_cost: r.planning_gross_cost})), null, 2) ); 

  return accrualData.reduce((currentTotals, row) => {
    const planning = Number(row.planning_gross_cost) || 0;
    const givenFromRow = Number(row.given) || 0; 
    
    // console.log(`[calculateTotals] Processing row (id: ${row.id || 'N/A'}): DB raw given: '${row.given}', Parsed given: ${givenFromRow}`);

    const actualForRow = calculateActualGrossCost(row); 
    const unusedForRow = calculateUnused(row); 
    const savingForRow = (row.provisional_saving !== undefined && row.provisional_saving !== null)
      ? (Number(row.provisional_saving) || 0)
      : calculateSaving(row, actualForRow);

    const nextTotals = {
      planning: currentTotals.planning + planning,
      actual: currentTotals.actual + actualForRow, 
      unused: currentTotals.unused + unusedForRow, 
      saving: currentTotals.saving + savingForRow, 
      given: currentTotals.given + givenFromRow, 
    };
    // console.log(`[calculateTotals] Row (id: ${row.id || 'N/A'}) done. currentTotals.given: ${currentTotals.given}, givenFromRow: ${givenFromRow}, nextTotals.given: ${nextTotals.given}`);
    return nextTotals;
  }, { planning: 0, actual: 0, unused: 0, saving: 0, given: 0 });
};

/**
 * Check if status constraints are met
 * @param {Object} data - Campaign or media type data
 * @returns {Object} Validation result with isValid flag and error messages
 */
export const validateStatusConstraints = (data) => {
  const result = {
    isValid: true,
    errors: [],
  };

  const { 
    campaign_status: status, 
    total_given: given, 
    planning_gross_cost: planning, 
    actual_gross_cost: actual,
    unused,
    saving,
  } = data;

  // Planning status constraints
  if (status === 'Planning' && given !== planning) {
    result.isValid = false;
    result.errors.push('For Planning status, Given must equal Planning.');
  }

  // On-going status constraints
  if (status === 'On-going' && (actual + unused) !== planning) {
    result.isValid = false;
    result.errors.push('For On-going status, Actual + Unused must equal Planning.');
  }

  // Done status constraints
  if (status === 'Done') {
    if (given !== planning) {
      result.isValid = false;
      result.errors.push('For Done status, Given must equal Planning.');
    }
    
    if (planning !== (actual + saving + unused)) {
      result.isValid = false;
      result.errors.push('For Done status, Planning must equal Actual + Saving + Unused.');
    }
  }

  return result;
};

/**
 * Generate months range between start and end dates
 * @param {Date|string} startDate - Start date 
 * @param {Date|string} endDate - End date
 * @returns {Array} Array of month strings in YYYY-MM format
 */
export const generateMonthsRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];

  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Ensure valid dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return [];
  }

  const months = [];
  const currentDate = new Date(start);
  currentDate.setDate(1); // Set to first day of month

  // Loop through months until we reach end date's month
  while (
    currentDate.getFullYear() < end.getFullYear() || 
    (currentDate.getFullYear() === end.getFullYear() && 
     currentDate.getMonth() <= end.getMonth())
  ) {
    const monthString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    months.push(monthString);
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return months;
};

/**
 * Determine campaign status based on start/end dates and current month - 2 logic
 * Business Rule: Use current month - 2 for status calculation
 * @param {Object} campaign - The campaign object with start_date and end_date
 * @returns {string} The campaign status: 'PLANNING', 'ON-GOING', or 'DONE'
 */
export const determineCampaignStatus = (campaign) => {
  if (!campaign || !campaign.start_date || !campaign.end_date) {
    return 'PLANNING'; // Default to planning if dates are missing
  }
  
  const today = new Date();
  const actualCurrentMonth = today.getMonth() + 1; // 1-based (June = 6)
  const currentYear = today.getFullYear();
  
  // Business Rule: current month - 2 for calculation (n-2)
  const calculationCurrentMonth = actualCurrentMonth - 2;
  let calculationYear = currentYear;
  
  // Handle year rollover for negative months
  if (calculationCurrentMonth <= 0) {
    calculationYear = currentYear - 1;
    const adjustedMonth = calculationCurrentMonth + 12; // Convert negative to previous year months
    const currentMonthDate = new Date(calculationYear, adjustedMonth - 1, 15); // Mid of calculation month
  } else {
    var currentMonthDate = new Date(calculationYear, calculationCurrentMonth - 1, 15); // Mid of calculation month
  }
  
  const campaignStart = new Date(campaign.start_date);
  const campaignEnd = new Date(campaign.end_date);
  
  // Remove time components for accurate date comparison
  currentMonthDate.setHours(0, 0, 0, 0);
  campaignStart.setHours(0, 0, 0, 0);
  campaignEnd.setHours(23, 59, 59, 999);
  
  if (currentMonthDate > campaignEnd) {
    return 'DONE'; // Calculation date has passed end date
  } else if (currentMonthDate >= campaignStart && currentMonthDate <= campaignEnd) {
    return 'ON-GOING'; // Calculation date is within campaign period
  } else {
    return 'PLANNING'; // Calculation date is before start date
  }
};

/**
 * Determine campaign status for snapshot based on snapshot month compared to campaign dates
 * @param {Object} campaign - The campaign object with start_date and end_date
 * @param {string} snapshotMonth - The snapshot month in YYYY-MM format (e.g., "2025-01")
 * @returns {string} The campaign status: 'PLANNING', 'ON-GOING', or 'DONE'
 */
export const determineSnapshotCampaignStatus = (campaign, snapshotMonth) => {
  if (!campaign || !snapshotMonth) {
    return 'PLANNING'; // Default to planning if parameters are missing
  }
  
  // Get campaign dates with enhanced field mapping
  const startDateValue = campaign.start_date || 
                        campaign.Start_Date || 
                        campaign.start || 
                        campaign.Start;
  const endDateValue = campaign.end_date || 
                      campaign.End_Date || 
                      campaign.end || 
                      campaign.End;
  
  if (!startDateValue || !endDateValue) {
    return 'PLANNING'; // Default to planning if dates are missing
  }
  
  try {
    // Parse snapshot month and use LAST DAY of snapshot month (consistent with N-2 logic)
    const [year, month] = snapshotMonth.split('-').map(Number);
    // Get last day of the snapshot month
    const referenceDate = new Date(year, month, 0); // month (not month-1) gives last day of previous month
    
    const startDate = new Date(startDateValue);
    const endDate = new Date(endDateValue);
    
    // Set time components for accurate date comparison
    referenceDate.setHours(23, 59, 59, 999); // End of snapshot month
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    console.log(`📅 Snapshot Status Logic - Campaign: ${campaign.name || campaign.campaign_name}`);
    console.log(`📅 Snapshot Month: ${snapshotMonth}, Last Day: ${referenceDate.toISOString().split('T')[0]}`);
    console.log(`📅 Start: ${startDate.toISOString().split('T')[0]}, End: ${endDate.toISOString().split('T')[0]}`);
    
    if (referenceDate > endDate) {
      console.log('✅ Status: DONE (snapshot month end > campaign end)');
      return 'DONE'; // Campaign had ended by end of snapshot month
    } else if (referenceDate >= startDate && referenceDate <= endDate) {
      console.log('🔄 Status: ON-GOING (snapshot month end within campaign period)');
      return 'ON-GOING'; // Campaign was active at end of snapshot month
    } else {
      console.log('📋 Status: PLANNING (snapshot month end < campaign start)');
      return 'PLANNING'; // Campaign hadn't started by end of snapshot month
    }
  } catch (error) {
    console.error('❌ Error determining snapshot campaign status:', error);
    return 'PLANNING'; // Default to planning on error
  }
};

/**
 * Calculate saving based on campaign status - only for 'done' campaigns
 * @param {Object} row - The accrual data row object
 * @param {Object} campaign - The campaign object to determine status
 * @param {number} calculatedActualGrossCost - The pre-calculated actual gross cost for the row
 * @param {boolean} forceCalculate - Force calculate saving regardless of status (for debugging)
 * @param {string} snapshotMonth - Optional snapshot month for snapshot-based status determination
 * @returns {number} The saving amount (0 if campaign is not 'done')
 */
export const calculateSavingBasedOnStatus = (row, campaign, calculatedActualGrossCost, forceCalculate = false, snapshotMonth = null) => {
  if (!row) return 0;
  const planning = Number(row.planning_gross_cost) || 0;
  return planning - calculatedActualGrossCost;
};

/**
 * Calculate check value: NTW - sum of other regions
 * @param {Object} row - The accrual data row object with regional data
 * @returns {Object} Object with value, display text, and style
 */
export const calculateCheck = (row) => {
  if (!row) return { value: 0, display: 'OK', style: {} };
  
  const ntw = Number(row.ntw) || 0;
  const north = Number(row.north) || 0;
  const centralMid = Number(row.central_mid) || 0;
  const restOfCentral = Number(row.the_rest_of_central) || 0;
  const ghcm = Number(row.ghcm) || 0;
  const mekong = Number(row.mekong) || 0;
  
  const sumOtherRegions = north + centralMid + restOfCentral + ghcm + mekong;
  const checkValue = ntw - sumOtherRegions;
  
  return {
    value: checkValue,
    display: checkValue === 0 ? 'OK' : formatCurrency(checkValue),
    style: checkValue !== 0 ? { 
      border: '2px solid red',
      borderRadius: '4px',
      padding: '2px 4px',
      backgroundColor: '#ffebee'
    } : {}
  };
};

/**
 * Calculate saving percentage and determine warning style
 * @param {Object} row - The accrual data row object
 * @param {Object} campaign - The campaign object to determine status
 * @param {number} calculatedActualGrossCost - The pre-calculated actual gross cost for the row
 * @param {boolean} forceCalculate - Force calculate percentage regardless of status
 * @param {string} snapshotMonth - Optional snapshot month for snapshot-based status determination
 * @returns {Object} Object with percentage, display text, and style
 */
export const calculateSavingPercentage = (row, campaign, calculatedActualGrossCost, forceCalculate = false, snapshotMonth = null) => {
  if (!row) return { percentage: 0, display: '-', style: {} };
  const planning = Number(row.planning_gross_cost) || 0;
  if (planning === 0) return { percentage: 0, display: '0%', style: {} };
  const saving = planning - calculatedActualGrossCost;
  const percentage = (saving / planning) * 100;
  return {
    percentage: percentage,
    display: `${percentage.toFixed(1)}%`,
    style: {}
  };
}; 